import { useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { ArrowRight, FileText, GitBranch, MessageSquare, Network, BookOpen, UserRound } from 'lucide-react'
import { ProductBrand, ServiceFeatures } from '@aide/ui'
import { useAuth } from '../hooks/useAuth'

export default function LoginView() {
  const errorRef = useRef<HTMLDivElement>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  
  const { authenticated, login } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const redirectError = useMemo(() => new URLSearchParams(location.search).get('error'), [location.search])
  const returnTo = useMemo(() => {
    const state = location.state as { from?: string } | null
    return state?.from || '/'
  }, [location.state])

  useEffect(() => {
    setError(redirectError)
  }, [redirectError])

  useEffect(() => {
    if (authenticated) {
      navigate('/', { replace: true })
    }
  }, [authenticated, navigate])

  useEffect(() => {
    if (error) {
      errorRef.current?.focus({ preventScroll: true })
      errorRef.current?.scrollIntoView?.({ block: 'center', behavior: 'instant' })
    }
  }, [error])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    setLoading(true)
    setError(null)

    try {
      await login(returnTo)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="product-site">
      <div className="product-container">
        <header className="product-nav">
          <a href="#" aria-label="Contextful home"><ProductBrand product="contextful" /></a>
          <nav aria-label="Main navigation"><a className="product-secondary-nav" href="#knowledge">The idea</a><a href="#access">Sign in <span aria-hidden="true">↗</span></a></nav>
        </header>
        <main>
          <section className="product-hero">
            <div>
              <span className="product-kicker">Connected knowledge · Human perspective</span>
              <h1>Your company knows more <em>than its documents.</em></h1>
              <p className="product-lead">Contextful brings source knowledge and human insight together. The facts, the connections, and the experience behind them have a place your team and agents can return to.</p>
              <div className="product-actions"><a className="product-button" href="#access">Open Contextful <ArrowRight size={16} aria-hidden="true" /></a><a href="#knowledge">A closer look <span aria-hidden="true">↓</span></a></div>
            </div>
            <div className="product-example" aria-label="Illustrative source knowledge example">
              <div className="product-example-label"><span>Knowledge, in context</span><span>Illustration</span></div>
              <h2>The document says what.<br />The people know why.</h2>
              <div className="example-source"><FileText aria-hidden="true" /><div><strong>A product specification</strong><span>The requirements and the revision they belong to.</span></div></div>
              <div className="example-source"><GitBranch aria-hidden="true" /><div><strong>An engineering decision</strong><span>The change, its history, and the reason behind it.</span></div></div>
              <div className="example-source"><MessageSquare aria-hidden="true" /><div><strong>A colleague’s experience</strong><span>The exception that never made it into the manual.</span></div></div>
              <div className="example-note"><strong>THE HUMAN CONTEXT</strong>“This applies to the new controller. The earlier model needs a different calibration.”</div>
            </div>
          </section>
          <section id="knowledge" className="product-section">
            <span className="product-kicker">A place for the whole picture</span>
            <h2>Knowledge with its origins intact.</h2>
            <ServiceFeatures>
              <article><BookOpen aria-hidden="true" /><h3>Grounded in sources</h3><p>Published knowledge retains its source and revision, so the material behind an answer remains within reach.</p></article>
              <article><UserRound aria-hidden="true" /><h3>Enriched by people</h3><p>Attributed notes capture explanations, exceptions, and experience alongside the knowledge they relate to.</p></article>
              <article><Network aria-hidden="true" /><h3>Useful beyond a document</h3><p>Selected knowledge and notes form portable context for agent tasks and engineering work in Forge.</p></article>
            </ServiceFeatures>
          </section>
          <section id="access" className="product-hero product-section">
            <div><span className="product-kicker">Your workspace</span><h2 style={{ marginBottom: 0 }}>A shared purpose.<br />Your own perspective.</h2><p className="product-lead">Projects keep your sources, conversations, and contributions together. Your account determines the workspaces available to you.</p></div>
            <form onSubmit={handleSubmit} className="product-auth">
              <ProductBrand product="contextful" compact />
              <h2>Welcome to Contextful</h2><p>Your organization’s knowledge workspace.</p>
              {error && <div ref={errorRef} tabIndex={-1} role="alert" className="product-alert">{error}</div>}
              <button type="submit" className="product-button" disabled={loading}>{loading ? 'Redirecting…' : 'Sign in'}<ArrowRight size={16} aria-hidden="true" /></button>
              <p className="product-subtle">Continue with your organization’s sign-in.</p>
            </form>
          </section>
        </main>
        <footer className="product-footer"><ProductBrand product="contextful" /><span>Connected knowledge. Human perspective.</span></footer>
      </div>
    </div>
  )
}
